import {Autocomplete, AutocompleteRenderInputParams, TextField} from "@mui/material";
import React, {useCallback, useMemo} from "react";
import {useKeyVaultStore} from "../../api/KeyVaultStore";
import {BUILTIN_COLORS, KeyVault, KeyVaultRef} from "../../api/Model";
import {TextFieldProps} from "@mui/material/TextField/TextField";
import {autocompleteChangeEvent, SetState} from "../../util/FormHandlers";
import {idFn} from "../../util/FunctionUtil";
import {useRemoteValue} from "../../util/RemoteValue";

import {useAccess} from "../../util/access/UseAccess";

export function KeyVaultSelect<KV extends KeyVaultRef>({keyVault, setKeyVault, disableForVaultManager = true, ...props}: {
    keyVault: KV,
    setKeyVault: SetState<KV>,
    disableForVaultManager?: boolean,
} & TextFieldProps) {
    const keyVaultStore = useKeyVaultStore();
    const keyVaults = useRemoteValue(keyVaultStore.remoteValue, []) as any as KV[];
    const access = useAccess();
    const disabled = disableForVaultManager && access.accessibleVaults.canAccessSingleVault();

    const handleKeyVaultChange = useMemo(
        () => autocompleteChangeEvent<KV>(newKeyVault => setKeyVault(newKeyVault)),
        [setKeyVault],
    );

    const renderInput = useCallback(
        (params: AutocompleteRenderInputParams) => (
            <TextField {...params} {...props} />
        ), [props]);

    const optionLabel = useCallback(
        (option: KeyVaultRef) => option.name,
        [],
    );

    return (
        <Autocomplete<KV, false, true>
            options={keyVaults}
            disabled={disabled}
            renderInput={renderInput}
            getOptionKey={idFn}
            getOptionLabel={optionLabel}
            value={keyVault}
            onChange={handleKeyVaultChange}
            disableClearable={true}
        />
    );
}

export function KeyVaultSelectOpt({keyVault, setKeyVault, emptyLabel, ...props}: {
    keyVault: KeyVault | null,
    setKeyVault: SetState<KeyVault | null>,
    emptyLabel: string,
} & TextFieldProps) {
    const keyVaultStore = useKeyVaultStore();
    const keyVaults = useRemoteValue(keyVaultStore.remoteValue, []);

    const emptyOption = useMemo<KeyVault>(
        () => ({
            id: -1,
            name: emptyLabel,
            color: BUILTIN_COLORS.DEFAULT,
        }),
        [emptyLabel],
    );

    const handleKeyVaultChange = useMemo(
        () => autocompleteChangeEvent<KeyVault>(newKeyVault => setKeyVault(newKeyVault === emptyOption ? null : newKeyVault)),
        [setKeyVault, emptyOption],
    );

    const renderInput = useCallback(
        (params: AutocompleteRenderInputParams) => (
            <TextField {...params} {...props} />
        ), [props]);

    const optionLabel = useCallback(
        (option: KeyVaultRef) => option.name,
        [],
    );

    const options = useMemo(
        () => [emptyOption, ...keyVaults],
        [keyVaults, emptyOption],
    );

    return (
        <Autocomplete<KeyVault, false, true>
            options={options}
            renderInput={renderInput}
            getOptionKey={idFn}
            getOptionLabel={optionLabel}
            value={keyVault || emptyOption}
            onChange={handleKeyVaultChange}
            disableClearable={true}
        />
    );
}
